'use client';

import { cn } from '@/lib/utils';
import { BriefingPeriod, BriefingStatus, MarketBriefingPeriod } from './market-briefing-types';
import { CompactAudioPlayer } from './compact-audio-player';
import { Coffee, TrendingUp, Sunset, type LucideIcon } from 'lucide-react';
import { getDailyQuote } from './market-briefing-quotes';

interface MarketBriefingCardProps {
  period: MarketBriefingPeriod;
  isActive: boolean;
  onClick: () => void;
  audioSrc?: string;
  isPlayerEnabled?: boolean;
  isPlaying?: boolean;
  onPlayStateChange?: (playing: boolean) => void;
}

const gradientClasses: Record<BriefingPeriod, string> = {
  morning: 'before:bg-gradient-to-br before:from-[#d4a853] before:to-[#e8c679]',
  midday: 'before:bg-gradient-to-br before:from-[#4a9eff] before:to-[#6eb5ff]',
  evening: 'before:bg-gradient-to-br before:from-[#8b7cf6] before:to-[#a594f9]'
};

const accentColors: Record<BriefingPeriod, string> = {
  morning: '#d4a853',
  midday: '#4a9eff',
  evening: '#8b7cf6'
};

const iconComponents: Record<BriefingPeriod, LucideIcon> = {
  morning: Coffee,
  midday: TrendingUp,
  evening: Sunset
};

function StatusBadge({ status }: { status: BriefingStatus }) {
  const baseClasses = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide';

  switch (status) {
    case 'current':
      return (
        <span className={cn(baseClasses, 'bg-[#3fb950]/15 text-[#3fb950] border border-[#3fb950]/20')}>
          <span className="w-1.5 h-1.5 bg-[#3fb950] rounded-full animate-pulse" />
          CURRENT
        </span>
      );
    case 'upcoming':
      return (
        <span className={cn(baseClasses, 'bg-[#58a6ff]/15 text-[#58a6ff] border border-[#58a6ff]/20')}>
          Soon
        </span>
      );
    case 'completed':
    default:
      return (
        <span className={cn(baseClasses, 'bg-slate-700/30 text-[#6e7681] border border-slate-700/30')}>
          Done
        </span>
      );
  }
}

export function MarketBriefingCard({
  period,
  isActive,
  onClick,
  audioSrc,
  isPlayerEnabled = false,
  isPlaying = false,
  onPlayStateChange
}: MarketBriefingCardProps) {
  const accentColor = accentColors[period.id];
  // Get daily quote for this period (consistent throughout the day)
  const dailyQuote = getDailyQuote(period.id);

  return (
    <div
      className={cn(
        'relative p-4 rounded-xl transition-all duration-300',
        'bg-[#161b22]/50 border',
        'before:content-[""] before:absolute before:inset-0 before:rounded-xl before:opacity-10 before:z-0',
        gradientClasses[period.id],
        isActive
          ? 'border-[#d4a853]/50 before:opacity-20 shadow-lg shadow-[#d4a853]/5'
          : 'border-slate-700/30 hover:border-slate-600/50 hover:-translate-y-0.5',
      )}
    >
      <div className="relative z-10">
        {/* Header Row: Icon | Title | Status Badge */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            {(() => {
              const IconComponent = iconComponents[period.id];
              return <IconComponent size={20} className="text-[#e6edf3]" color={accentColor} />;
            })()}
            <div className="font-display text-reading-base font-semibold text-[#e6edf3]">{period.name}</div>
          </div>
          <StatusBadge status={period.status} />
        </div>

        {/* Audio Player Row */}
        <div className="mb-3" onClick={(e) => e.stopPropagation()}>
          <CompactAudioPlayer
            audioSrc={audioSrc}
            accentColor={accentColor}
            isEnabled={isPlayerEnabled}
            isPlaying={isPlaying}
            onPlayStateChange={onPlayStateChange}
          />
        </div>

        {/* Footer: Time Range + Tagline (stacked) */}
        <div className="space-y-2">
          {/* Time Range */}
          <div className="flex items-center justify-start">
            <div
              className="text-reading-xs font-mono px-2.5 py-1 rounded-md font-medium"
              style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
            >
              {period.timeRange}
            </div>
          </div>

          {/* Daily Quote - Gets its own line */}
          <div
            className="text-reading-xs text-[#8b949e] italic leading-relaxed"
            style={{
              borderLeft: `2px solid ${accentColor}30`,
              paddingLeft: '0.75rem'
            }}
          >
            {dailyQuote}
          </div>
        </div>
      </div>
    </div>
  );
}
